import React, { useState, useEffect, useRef } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { useManualQuery, useMutation } from "graphql-hooks";
import { useNavigate, Link } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import dayjs from "dayjs";

import {
  TextField,
  Box,
  Divider,
  Grid,
  Typography,
  Tooltip,
  IconButton,
  Avatar,
  Select,
  MenuItem,
} from "@mui/material";

import { ALL_USERS_QUERY, UPDATE_USER_STATUS_MUTATION } from "../graphQL/user_qry";

import AlertPopup from "../components/AlertPopup.js";
import { styled } from "@mui/material/styles";

import {
  AssignmentTurnedInOutlined,
  FactCheckOutlined,
  VisibilityOutlined,
  DeleteOutlined,
  EditOutlined,
  ArrowDropDown,
} from "@mui/icons-material";

// Import toy images
import photo1 from "../assets/ToysList/photo1.png";
import photo2 from "../assets/ToysList/photo2.png";
import photo3 from "../assets/ToysList/photo3.png";
import photo4 from "../assets/ToysList/photo4.png";
import photo5 from "../assets/ToysList/photo5.png";
import photo6 from "../assets/ToysList/photo6.png";

// Status Options
const STATUS_OPTIONS = ["All", "Active", "Inactive"];

//Tooltip Component
const StyledTooltip = ({ title, children }) => (
  <Tooltip
    title={title}
    placement="top"
    arrow
    PopperProps={{
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, -5],
          },
        },
      ],
    }}
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: "#666",
          color: "white",
          fontSize: "12px",
          fontWeight: "500",
          borderRadius: "4px",
          padding: "6px 8px",
          "& .MuiTooltip-arrow": {
            color: "#666",
          },
        },
      },
    }}
  >
    {children}
  </Tooltip>
);

// UserLink Component
const UserLink = ({ username, userpath }) => (
  <StyledTooltip title="View User's Page">
    <Link
      to={`${userpath}/profile`}
      style={{
        color: "red",
        textDecoration: "none",
        fontSize: "14px",
        display: "inline-block",
        lineHeight: 1,
      }}
      target="blank"
      onMouseEnter={e => {
        e.target.style.textDecoration = "underline";
      }}
      onMouseLeave={e => {
        e.target.style.textDecoration = "none";
      }}
    >
      {username}
    </Link>
  </StyledTooltip>
);

const RootDiv = styled("div")({
  marginRight: "8rem",
  textAlign: "center",
  padding: 2,
  paddingLeft: 0,
  paddingRight: 0,
  width: "1400px",
  //  minWidth: "500px",
});

const ContainerDiv = styled("div")({
  padding: 32,
  marginBottom: "5rem",
  clear: "both",
  borderRadius: "8px",
  minHeight: "870px",
  backgroundColor: "#ffffff",
});

export default function AdminUsersList() {
  const navigate = useNavigate();
  const [isPaginationMenuOpen, setIsPaginationMenuOpen] = useState(false);
  const paginationSelectRef = useRef(null);
  const paginationMenuRef = useRef(null);
  // Alert popup state
  const [alertPopup, setAlertPopup] = useState({
    open: false,
    type: "",
    userUuid: null,
  });

  const [statusFilter, setStatusFilter] = useState("All");
  const [users, setUsers] = useState([]);
  const [rows, setRows] = useState([]);

  const [getAllUsersData] = useManualQuery(ALL_USERS_QUERY, {
    fetchPolicy: "network-only",
  });
  const setUsersList = async () => {
    const {
      loading: loadingUserListData,
      error: errorUserListData,
      data: userListData,
    } = await getAllUsersData();
    if (!loadingUserListData && userListData && userListData.allUsers) {
      console.log(userListData.allUsers);
      setUsers(
        userListData.allUsers.map(user => ({
          id: user.id,
          userUuid: user.userUuid,
          username: user.username,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          dateJoined: dayjs(user.dateJoined).format("MMM D, YYYY"),
          status: user.isActive ? "Active" : "Inactive",
          isAdmin: user.isSuperuser ? "Yes" : "No",
          //photoName: user.fileName?.split("/").pop(),
          //photoSize: user.fileSize ? (user.fileSize/ 1024).toFixed(0): ""
        }))
      );
    } else {
      console.log("errorUserListData: ", errorUserListData);
    }
  };

  useEffect(() => {
    (async () => setUsersList())();
  }, []);

  // Initialize data
  useEffect(() => {
    setRows(getFilteredData);
  }, [statusFilter, users]);

  const getFilteredData = () => {
    let filteredData = users;
    if (statusFilter !== "All") {
      if (statusFilter === "Active") {
        filteredData = filteredData.filter(user => user.status === "Active");
      } else {
        filteredData = filteredData.filter(user => user.status === "Inactive");
      }
    }
    console.log(filteredData);
    return filteredData;
  };

  // Status filter change handlers
  const handleStatusFilterChange = event => {
    setStatusFilter(event.target.value);
  };

  const dateFormatter = new Intl.DateTimeFormat("en-us", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  const formatter = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true, // change to false if you prefer 24h format
  });

  const dayFormat = day => {
    if (day) {
      const dayDate = new Date(day);
      return formatter.format(dayDate);
    } else return "";
  };

  const dateFormat = date => {
    if (date) {
      const dayDate = new Date(date);
      return dateFormatter.format(dayDate);
    } else {
      return "";
    }
  };

  // Popup handlers
  const handleActivateClick = userUuid => {
    setAlertPopup({
      open: true,
      type: "activate",
      userUuid: userUuid,
    });
  };

  const handleDeactivateClick = userUuid => {
    setAlertPopup({
      open: true,
      type: "deactivate",
      userUuid: userUuid,
    });
  };

  const handleDeleteClick = userUuid => {
    setAlertPopup({
      open: true,
      type: "delete",
      userUuid: userUuid,
    });
  };

  const [updateUserStatus] = useMutation(UPDATE_USER_STATUS_MUTATION, {
    fetchPolicy: "network-only",
  });
  const handleConfirmAction = async () => {
    let isActive = false;
    if (alertPopup.type === "activate") {
      isActive = true;
    } else if (alertPopup.type === "deactivate") {
      isActive = false;
    }
    const {
      loading: loadingUserStatus,
      error: errorUserStatus,
      data: userStatus,
    } = await updateUserStatus({
      variables: {
        userUuid: alertPopup.userUuid,
        isActive: isActive,
      },
    });
    if (!loadingUserStatus && userStatus && userStatus.updateUserStatus) {
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user.userUuid === alertPopup.userUuid
            ? {
                ...user,
                status: isActive ? "Active" : "Inactive",
              }
            : user
        )
      );
    } else {
      console.log("errorUserStatus: ", errorUserStatus);
    }
    setAlertPopup({ open: false, type: "", userUuid: null });
  };

  const handleCancelAction = () => {
    setAlertPopup({ open: false, type: "", userUuid: null });
  };

  const columns = [
    {
      field: "username",
      headerName: "Username",
      minWidth: 120,
      flex: 1,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: rows => {
        let userPath = "/user/" + rows.row.userUuid;
        return <UserLink username={rows.row.username} userpath={userPath} />;
      },
    },
    {
      field: "fullName",
      headerName: "Full Name",
      minWidth: 120,
      flex: 1,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: rows => (
        <div
          style={{
            whiteSpace: "normal", // ⬅️ allows wrapping
            wordBreak: "break-word",
            lineHeight: "1.2",
            textAlign: "left",
            padding: "0 4px",
          }}
        >
          {rows.row.firstName} {rows.row.lastName}
        </div>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      minWidth: 10,
      flex: 1,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: rows => (
        <div
          style={{
            whiteSpace: "normal", // ⬅️ allows wrapping
            wordBreak: "break-word",
            lineHeight: "1.2",
            textAlign: "left",
            padding: "0 4px",
          }}
        >
          {rows.row.email}
        </div>
      ),
    },
    {
      field: "dateJoined",
      headerName: "Date Joined",
      minWidth: 120,
      flex: 1,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: rows => {
        return (
          <StyledTooltip title={dayFormat(rows.row.dateJoined)} arrow>
            <span
              style={{
                display: "inline-block",
                lineHeight: 1.5,
                verticalAlign: "middle",
              }}
            >
              {dateFormat(rows.row.dateJoined)}
            </span>
          </StyledTooltip>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 20,
      flex: 1,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: rows => {
        return (
          <Box
            sx={{
              backgroundColor: "white",
              color: "#333",
              border: "1px solid #000",
              fontSize: "12px",
              fontWeight: "500",
              width: "80px",
              height: "30px",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            {rows.row.status}
          </Box>
        );
      },
    },
    {
      field: "isAdmin",
      headerName: "Is Admin?",
      minWidth: 20,
      flex: 1,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: rows => {
        return (
          <Box
            sx={{
              backgroundColor: "white",
              color: "#333",
              border: "1px solid #000",
              fontSize: "12px",
              fontWeight: "500",
              width: "80px",
              height: "30px",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            {rows.row.isAdmin}
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 100,
      flex: 1,
      sortable: false,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: rows => {
        return (
          <Box
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {rows.row.status === "Inactive" ? (
              <StyledTooltip title="Activate this user">
                <IconButton
                  size="small"
                  onClick={() => handleActivateClick(rows.row.userUuid)}
                  sx={{
                    color: "#666",
                    "&:hover": {
                      backgroundColor: "rgba(0,0,0,0.04)",
                    },
                  }}
                >
                  <AssignmentTurnedInOutlined fontSize="small" />
                </IconButton>
              </StyledTooltip>
            ) : (
              <StyledTooltip title="Deactivate this user">
                <IconButton
                  size="small"
                  onClick={() => handleDeactivateClick(rows.row.userUuid)}
                  sx={{
                    color: "#666",
                    "&:hover": {
                      backgroundColor: "rgba(0,0,0,0.04)",
                    },
                  }}
                >
                  <FactCheckOutlined fontSize="small" />
                </IconButton>
              </StyledTooltip>
            )}
            <StyledTooltip title="View this user">
              <IconButton
                size="small"
                sx={{
                  color: "#666",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.04)",
                  },
                  mt: 1,
                }}
              >
                <Link
                  component={RouterLink}
                  to="/TBD"
                  target="_blank"
                  color="black"
                  underline="none"
                  sx={{
                    color: "#666",
                    "&:hover": {
                      backgroundColor: "rgba(0,0,0,0.04)",
                    },
                  }}
                >
                  <VisibilityOutlined fontSize="small" />
                </Link>
              </IconButton>
            </StyledTooltip>
            <StyledTooltip title="Edit this user">
              <IconButton
                size="small"
                sx={{
                  color: "#666",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.04)",
                  },
                }}
              >
                <Link
                  component={RouterLink}
                  to="/TBD"
                  target="_blank"
                  color="black"
                  underline="none"
                  sx={{
                    color: "#666",
                    "&:hover": {
                      backgroundColor: "rgba(0,0,0,0.04)",
                    },
                  }}
                >
                  <EditOutlined fontSize="small" />
                </Link>
              </IconButton>
            </StyledTooltip>
            <StyledTooltip title="Delete this user">
              <IconButton
                size="small"
                onClick={() => alert("TBD")}
                sx={{
                  color: "#666",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.04)",
                  },
                }}
              >
                <DeleteOutlined fontSize="small" />
              </IconButton>
            </StyledTooltip>
          </Box>
        );
      },
    },
  ];

  function DataGridTitle() {
    return (
      <>
        <Grid
          container
          xs={12}
          sx={{
            pt: 2,
            pb: 2,
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
          }}
        >
          <Typography
            variant="h1"
            sx={{
              fontSize: 20,
              fontWeight: 500,
              fontFamily: "Roboto, Helvetica, Arial, sans-serif",
              margin: "0 auto",
            }}
          >
            Users List
          </Typography>
          <TextField
            select
            label="Status"
            value={statusFilter}
            onChange={handleStatusFilterChange}
            sx={{
              width: "170px",
              paddingRight: 2,
              "& .MuiSelect-select": {
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
              },
              "& .MuiOutlinedInput-root": {
                height: "40px",
                backgroundColor: "white",
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e0e0e0",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e0e0e0",
                  borderWidth: "1px",
                },
              },
              "& .MuiInputLabel-root": {
                color: "#666",
                fontSize: "14px",
                "&.Mui-focused": {
                  color: "#666",
                },
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#e0e0e0",
              },
            }}
            SelectProps={{
              IconComponent: ArrowDropDown,
              MenuProps: {
                PaperProps: {
                  sx: {
                    "& .MuiMenuItem-root": {
                      justifyContent: "flex-start",
                      textAlign: "left",
                    },
                  },
                },
              },
            }}
          >
            {STATUS_OPTIONS.map(option => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Divider />
      </>
    );
  }

  useEffect(() => {
    const handleScroll = event => {
      if (isPaginationMenuOpen) {
        const isScrollingInsideDropdown =
          (paginationMenuRef.current &&
            paginationMenuRef.current.contains(event.target)) ||
          (paginationSelectRef.current &&
            paginationSelectRef.current.contains(event.target));

        if (!isScrollingInsideDropdown) {
          setIsPaginationMenuOpen(false);
        }
      }
    };

    if (isPaginationMenuOpen) {
      document.addEventListener("scroll", handleScroll, true);
    }

    return () => {
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, [isPaginationMenuOpen]);

  const alertConfig = {
    activate: {
      title: "Activate User",
      message: "Are you sure you want to activate this user?",
      confirmText: "YES, ACTIVATE",
    },
    deactivate: {
      title: "Deactivate User",
      message: "Are you sure you want to deactivate this user?",
      confirmText: "YES, DEACTIVATE",
    },
    delete: {
      title: "Delete User",
      message: "Are you sure you want to delete this user?",
      confirmText: "YES, DELETE",
    },
  };

  return (
    <RootDiv>
      <title>R3PLAY - Report Management – Admin</title>
      <ContainerDiv>
        <DataGrid
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              borderBottom: "1px solid rgba(224, 224, 224, 1) !important",
              position: "relative",
              zIndex: 1,
            },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center",
            },
            // Firefox-specific rendering fix
            "@-moz-document url-prefix()": {
              "& .MuiDataGrid-columnHeaders": {
                transform: "translateY(0.1px)",
              },
            },
            "& .MuiDataGrid-cell:focus-within": {
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader:last-child .MuiDataGrid-columnSeparator": {
              display: "none",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 700,
              fontSize: 12,
            },
          }}
          columns={columns}
          rows={rows}
          showCellRightBorder={true}
          slots={{ toolbar: DataGridTitle }}
          getRowId={row => row.id}
          autoHeight={true}
          rowHeight={90}
          experimentalFeatures={{ newEditingApi: true }}
          pagination
          pageSizeOptions={[10, 20, 50]} // user can select page size
          initialState={{
            pagination: { paginationModel: { page: 0, pageSize: 10 } }, // default to 10 rows
            sorting: {
              sortModel: [{ field: "name", sort: "asc" }],
            },
          }}
          slotProps={{
            pagination: {
              SelectProps: {
                open: isPaginationMenuOpen,
                onOpen: () => setIsPaginationMenuOpen(true),
                onClose: () => setIsPaginationMenuOpen(false),
                ref: paginationSelectRef,
                MenuProps: {
                  PaperProps: {
                    ref: paginationMenuRef,
                  },
                },
              },
            },
          }}
        />
        {/* Alert Popup */}
        {alertPopup.type && (
          <AlertPopup
            open={alertPopup.open}
            onClose={handleCancelAction}
            {...alertConfig[alertPopup.type]}
            onConfirm={handleConfirmAction}
          />
        )}
      </ContainerDiv>
    </RootDiv>
  );
}
